const { Core, Utils, Cache } = Global;
const { $toggleDisplay, $replaceContent, $active, $loading } = Utils;

const RQ_SECTION = 'r_predictive-search';
const RQ_SECTION_SELECTOR = '[data-template]';
const ATTR_NO_RESULTS = 'data-empty-results';
const DEFAULT_MIN_CHARS = 3;
const DEFAULT_LIMIT = 5;
const DEBOUNCE_INTERVAL = 180;

customElements.define('predictive-search', class extends Core {
    propTypes = {
        'results-limit': Number
    }

    elements = {
        $: ['search-input', 'product-type-select', 'button', 'results', 'initial']
    }

    render() {
        this.$('search-input', {
            focus: this._handleFocus,
            input: this._scheduleInput
        });
        this.$('button', {
            click: this._handleButtonClick
        });
        this.$('product-type-select', {
            change: this._handleTypeSelect
        });
        this._handleOuterClick();
    }

    _handleTypeSelect() {
        this._handleInput();
        this.$('search-input').focus();
    }

    _handleButtonClick(e) {
        if (this.$('search-input').value) return;
        e.preventDefault();
        this.$('search-input').focus(); 
    }

    _handleFocus() {
        this.active = this.minQueryChars || !!this.$('initial');
    }

    async _handleInput() {
        const requestId = this.requestId = (this.requestId || 0) + 1;
        this.controller?.abort();
        if(!this.minQueryChars) {
            $loading(this, false);
            this.showInitial = true;
            this.showResults = false;
            return;
        }
        const query = this.query;
        const key = this._cacheKey(query);
        this.controller = new AbortController();
        $loading(this, true);
        try {
            await this._getQueryResults(query, this.controller.signal);
            if (requestId !== this.requestId || query !== this.query || !this.isConnected) return;
            this._renderQueryResults(key);
            this.active = true;
        } catch (error) {
            if (error.name !== 'AbortError' && requestId === this.requestId) {
                // The regular search form remains usable if suggestions fail.
                this.showResults = false;
                this.showInitial = true;
            }
        } finally {
            if (requestId === this.requestId) $loading(this, false);
        }
    }

    _scheduleInput() {
        // Invalidate immediately, not after the debounce: stale results must not
        // reopen a dropdown while the shopper is still typing/clearing it.
        this.requestId = (this.requestId || 0) + 1;
        this.controller?.abort();
        clearTimeout(this.inputTimer);
        this.inputTimer = setTimeout(() => this._handleInput(), DEBOUNCE_INTERVAL);
    }

    _cacheKey(query) {
        return `predictive-search:${this.limit}:${query}`;
    }

    async _getQueryResults(query = this.query, signal) {
        const key = this._cacheKey(query);
        if (!Cache.has(key)) {
            const $queryDoc = await this._fetchQueryDoc(query, signal);
            if (!$queryDoc) throw new Error('Missing predictive search results');
            Cache.set(key, {
                hasResults: !$queryDoc.hasAttribute(ATTR_NO_RESULTS),
                content: $queryDoc
            });
            this.cacheKeys = (this.cacheKeys || []).filter(existing => existing !== key);
            this.cacheKeys.push(key);
            if (this.cacheKeys.length > 32) Cache.delete(this.cacheKeys.shift());
        }
    }

    async _fetchQueryDoc(query, signal) {
        const url = new URL(routes.predictive_search_url, window.location.origin);
        url.search = new URLSearchParams({
            q: query, 'resources[limit]': this.limit, section_id: RQ_SECTION
        });
        const response = await fetch(url, { signal });
        if (!response.ok) throw new Error(`Search failed (${response.status})`);
        return Utils.parseHTML(await response.text()).querySelector(RQ_SECTION_SELECTOR);
    }

    _renderQueryResults(key = this._cacheKey(this.query)) {
        const currentQuery = Cache.get(key);

        $replaceContent(this.$('results'), currentQuery.content);

        this.showInitial = !currentQuery.hasResults;
        this.showResults = true;
    }

    _handleOuterClick() {
        this.outerClick = (e) => {
            if(!this.contains(e.target) && this.active && document.activeElement !== this.$('search-input')) {
                this.active = false;
            }
        };
        document.addEventListener('click', this.outerClick);
    }

    destroy() {
        clearTimeout(this.inputTimer);
        this.requestId = (this.requestId || 0) + 1;
        this.controller?.abort();
        document.removeEventListener('click', this.outerClick);
    }

    set showInitial(state) {
        if(!this.$('initial')) {
            this.active = false;
            return;
        }

        $toggleDisplay(this.$('initial'), state);
        this.active = state;
    }

    set showResults(state) {
        $toggleDisplay(this.$('results'), state);
    }

    set active(state) {
        if (state === this._active) {
            return;
        }

        $active(this, state);
        this._active = state;
    }

    get active() {
        return !!this._active;
    }

    get minQueryChars() {
        return this.$('search-input').value.trim().length >= DEFAULT_MIN_CHARS;
    }

    get query() {
        const inputValue = this.$('search-input').value.trim();
        if (this.$('product-type-select') && this.$('product-type-select').value !== "") {
            return `product_type:${this.$('product-type-select').value} AND ${inputValue}`
        }
        return inputValue;
    }
    
    get limit() {
        return this.prop('results-limit') || DEFAULT_LIMIT;
    }
});

class TypingEffectElement extends HTMLInputElement {
    constructor() {
      super();
      this.stopped = false;
    }
  
    static get observedAttributes() {
      return ['phrases'];
    }
  
    attributeChangedCallback(name, oldValue, newValue) {
      if (this.isConnected && !this.stopped) this._start();
    }
  
    connectedCallback() {
      this.addEventListener('input', this.stop);
      this._start();
    }
  
    disconnectedCallback() {
      clearTimeout(this.typingTimer);
      this.removeEventListener('input', this.stop);
    }

    _start() {
      clearTimeout(this.typingTimer);
      this.originalPlaceholder ??= this.getAttribute('placeholder') || '';
      if (this.stopped || this.value || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const phrases = (this.getAttribute('phrases') || '').split(',').map(s => s.trim()).filter(Boolean);
      if (!phrases.length) return;
      const delay = Number(this.getAttribute('typing-delay')) || 100;
      const repeatDelay = Number(this.getAttribute('repeat-delay')) * 1000 || 3000;
      let phrase = 0;
      let letter = 0;
      const tick = () => {
        if (this.stopped || !this.isConnected) return;
        this.setAttribute('placeholder', phrases[phrase].slice(0, ++letter));
        if (letter < phrases[phrase].length) {
          this.typingTimer = setTimeout(tick, delay);
        } else if (phrase < phrases.length - 1 || this.hasAttribute('loop')) {
          phrase = (phrase + 1) % phrases.length;
          letter = 0;
          this.typingTimer = setTimeout(tick, repeatDelay);
        }
      };
      tick();
    }
    stop() {
      this.stopped = true;
      clearTimeout(this.typingTimer);
      this.setAttribute('placeholder', this.originalPlaceholder || '');
    }
  }
  
customElements.define('typing-effect', TypingEffectElement, { extends: 'input' });
